/**
 * The feature → workspace mount contract (integration seam for UI-W2).
 *
 * A feature never imports the layout engine. It contributes a
 * `WorkspacePanelContent` keyed by a stable id; a preset places a
 * `PanelDescriptor` with that same id. The store owns geometry and panel state;
 * the feature owns the title (which it localises itself) and body. The registry
 * is passed to `WorkspaceView` at mount time.
 */
import type { ReactNode } from "react";

export interface WorkspacePanelContent {
  /** Stable id; must match the `PanelDescriptor.id` a preset/layout places. */
  readonly id: string;
  /** Display title. The owning feature is responsible for localising it. */
  readonly title: ReactNode;
  /** Accessible name for the panel region; defaults to `title` when a string. */
  readonly label?: string;
  /** Compact label for space-constrained chrome (mobile quick-panel chips). */
  readonly shortLabel?: string;
  /** Renders the panel body. Called only while the panel is expanded. */
  readonly render: () => ReactNode;
}

export type PanelContentRegistry = ReadonlyMap<string, WorkspacePanelContent>;

/** Builds a registry from a list of contributions. */
export function createPanelContentRegistry(
  contents: readonly WorkspacePanelContent[],
): PanelContentRegistry {
  return new Map(contents.map((content) => [content.id, content]));
}
